import api from '../axiosIntercepture';

function normalizeListResponse(body) {
  if (!body) return { rows: [], total: 0 };
  const inner = body.data !== undefined ? body.data : body;

  if (Array.isArray(inner)) {
    return { rows: inner, total: body.total ?? body.count ?? inner.length };
  }
  const arrayKeys = ['data', 'results', 'items', 'records', 'list', 'rows', 'docs'];
  for (const key of arrayKeys) {
    if (Array.isArray(inner?.[key])) {
      return {
        rows: inner[key],
        total: inner.total ?? inner.count ?? inner.totalCount ?? body.total ?? inner[key].length
      };
    }
  }
  return { rows: [], total: 0 };
}

/** Resolve Mongo / string id from various API shapes. */
export function extractStockTransferOutId(r) {
  if (!r || typeof r !== 'object') return '';
  const candidates = [
    r._id,
    r.id,
    r.stockTransferOutId,
    r.stoId,
    r.mongoId,
    r.uuid,
    r.transferId
  ];
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (typeof c === 'object' && c.$oid) return String(c.$oid);
  }
  return '';
}

/** Map API document to Stock Transfer Out list row shape. */
export function mapStockTransferOutListRow(r) {
  if (!r) return null;
  const id = extractStockTransferOutId(r);
  const idStr = id ? String(id) : '';
  return {
    /** DataGrid / actions — same id Mongo uses for GET /stock-transfer-out/:id */
    id: idStr,
    _id: idStr,
    transferNo: r.transferNo ?? r.transferNumber ?? r.stoNumber ?? r.reference ?? '—',
    sourceLocation: r.sourceLocation ?? r.sourceFacilityId ?? r.source?.name ?? '—',
    destinationLocation: r.destinationLocation ?? r.destinationFacilityId ?? r.destination?.name ?? '—',
    status: r.status ?? '—',
    totalQty: r.totalTransferOutQty ?? r.totalQty ?? 0,
    totalAmount: r.totalTransferOutAmount ?? r.totalAmount ?? 0,
    expectedDate: r.expectedDeliveryDate ?? r.expectedDate ?? '',
    createdAt: r.createdAt ?? r.created_date ?? ''
  };
}

/**
 * POST /api/stock-transfer-out
 */
export const createStockTransferOut = async (payload) => {
  try {
    const response = await api.post('stock-transfer-out', payload);
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to create stock transfer out');
    }
    return response.data?.data ?? response.data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to create stock transfer out';
    throw new Error(msg);
  }
};

/**
 * GET /api/stock-transfer-out?page=&limit=&status=
 * `location_id` header is set by axios interceptor from selected facility.
 */
export const fetchStockTransferOutList = async ({ page = 1, limit = 10, status = 'all' } = {}) => {
  try {
    const response = await api.get('stock-transfer-out', {
      params: { page, limit, status }
    });
    const { rows, total } = normalizeListResponse(response.data);
    return { rows: rows.map(mapStockTransferOutListRow).filter(Boolean), total };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to fetch stock transfer out list';
    throw new Error(msg);
  }
};

/**
 * GET /api/stock-transfer-out/by-destination
 * STOs destined for the current warehouse (`location_id` header / optional destinationLocationId).
 */
export const fetchStockTransferOutListByDestination = async ({
  page = 1,
  limit = 10,
  status = 'all',
  destinationLocationId
} = {}) => {
  try {
    const params = { page, limit, status };
    if (destinationLocationId) {
      params.destinationLocationId = destinationLocationId;
    }
    const response = await api.get('stock-transfer-out/by-destination', { params });
    const { rows, total } = normalizeListResponse(response.data);
    return { rows: rows.map(mapStockTransferOutListRow).filter(Boolean), total };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to fetch stock transfer out list by destination';
    throw new Error(msg);
  }
};

/**
 * Walk pages until all destination-filtered STO rows are loaded (dropdowns / pickers).
 */
export async function fetchAllStockTransferOutListByDestination({
  limit = 100,
  status = 'all',
  destinationLocationId,
  maxPages = 100
} = {}) {
  const seen = new Set();
  const aggregated = [];
  let page = 1;
  let reportedTotal = Infinity;

  while (page <= maxPages && aggregated.length < reportedTotal) {
    const { rows, total } = await fetchStockTransferOutListByDestination({
      page,
      limit,
      status,
      destinationLocationId
    });
    if (typeof total === 'number' && !Number.isNaN(total)) {
      reportedTotal = total;
    }
    const batch = rows || [];
    if (batch.length === 0) break;
    for (const r of batch) {
      const id = extractStockTransferOutId(r);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      aggregated.push(r);
    }
    if (batch.length < limit) break;
    page += 1;
  }

  return aggregated;
}

/**
 * Walk pages until all Stock Transfer Out rows are loaded (for dropdowns / pickers).
 */
export async function fetchAllStockTransferOutList({
  limit = 100,
  status = 'all',
  maxPages = 100
} = {}) {
  const seen = new Set();
  const aggregated = [];
  let page = 1;
  let reportedTotal = Infinity;

  while (page <= maxPages && aggregated.length < reportedTotal) {
    const { rows, total } = await fetchStockTransferOutList({ page, limit, status });
    if (typeof total === 'number' && !Number.isNaN(total)) {
      reportedTotal = total;
    }
    const batch = rows || [];
    if (batch.length === 0) break;
    for (const r of batch) {
      const id = extractStockTransferOutId(r);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      aggregated.push(r);
    }
    if (batch.length < limit) break;
    page += 1;
  }

  return aggregated;
}

/** Collect SKU rows from `item-by-sku` response (single object or list). */
function listSkuItemCandidatesFromResponse(body) {
  if (!body) return [];
  const inner = body.data !== undefined ? body.data : body;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(body?.results)) return body.results;
  if (Array.isArray(inner)) return inner;
  if (inner && typeof inner === 'object') return [inner];
  return [];
}

/** First row with a resolvable SKU and available qty > 0 (or unknown qty). */
function pickSkuItemRowWithAvailableQty(body) {
  const candidates = listSkuItemCandidatesFromResponse(body);
  if (candidates.length === 0) return { row: null, allZeroOrInvalid: false };

  const hadRowsWithSku = candidates.some((r) => Boolean(mapStockTransferOutSkuToItemDraft(r)?.skuId));

  const usable = candidates.filter((r) => {
    const m = mapStockTransferOutSkuToItemDraft(r);
    if (!m?.skuId) return false;
    const aq = m.availableQty;
    if (aq === '' || aq == null) return true;
    const n = Number(aq);
    if (Number.isNaN(n)) return true;
    return n > 0;
  });

  if (usable.length > 0) return { row: usable[0], allZeroOrInvalid: false };
  return { row: null, allZeroOrInvalid: hadRowsWithSku };
}

/** Map `item-by-sku` response to STO line item draft fields. */
export function mapStockTransferOutSkuToItemDraft(row) {
  if (!row) return null;
  const header = row.header ?? row;
  const originalPrice =
    row.originalPrice ??
    row.customerPricing?.originalPrice ??
    header?.originalPrice ??
    row.unitPrice ??
    '';
  const availableQty =
    row.availableQty ?? row.available_qty ?? row.qtyAvailable ?? header?.availableQty ?? '';
  return {
    skuId: header?.skuId ?? row.skuId ?? row.sku ?? '',
    description: header?.description ?? row.description ?? row.item_name ?? '',
    itemCube: header?.cube ?? header?.itemCube ?? row.itemCube ?? '',
    itemWeight: header?.netWeight ?? header?.itemWeight ?? row.itemWeight ?? row.netWeight ?? '',
    pack: header?.pack ?? row.pack ?? 'STD',
    uom: header?.uom ?? row.uom ?? 'EA',
    unitPrice:
      originalPrice !== '' && originalPrice != null ? String(originalPrice) : '',
    availableQty:
      availableQty !== '' && availableQty != null ? String(availableQty) : ''
  };
}

/**
 * POST /api/stock-transfer-out/item-by-sku
 * Resolves SKU at source location for STO line entry.
 */
export async function fetchStockTransferOutItemBySku(skuId, { sourceLocationId } = {}) {
  const trimmed = String(skuId ?? '').trim();
  if (!trimmed) throw new Error('SKU ID is required');

  try {
    const payload = { skuId: trimmed };
    if (sourceLocationId) payload.sourceLocationId = sourceLocationId;

    const response = await api.post('stock-transfer-out/item-by-sku', payload);
    if (response.data?.success === false) {
      throw new Error(response.data?.message || `No data found for SKU: ${trimmed}`);
    }

    const { row, allZeroOrInvalid } = pickSkuItemRowWithAvailableQty(response.data);
    if (!row) {
      if (allZeroOrInvalid) {
        throw new Error('Item qty is not available for this item.');
      }
      throw new Error(`No data found for SKU: ${trimmed}`);
    }

    const mapped = mapStockTransferOutSkuToItemDraft(row);
    if (!mapped?.skuId) {
      throw new Error(`No data found for SKU: ${trimmed}`);
    }

    return mapped;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to load SKU details';
    throw new Error(msg);
  }
}

/**
 * GET /api/stock-transfer-out/:mongoId
 */
export const getStockTransferOutById = async (mongoId) => {
  if (!mongoId) throw new Error('Missing stock transfer out id');
  try {
    const response = await api.get(`stock-transfer-out/${encodeURIComponent(String(mongoId))}`);
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to load stock transfer out');
    }
    return response.data?.data ?? response.data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to load stock transfer out';
    throw new Error(msg);
  }
};

/**
 * PUT /api/stock-transfer-out/:mongoId
 */
export const updateStockTransferOut = async (mongoId, payload) => {
  if (!mongoId) throw new Error('Missing stock transfer out id');
  try {
    const response = await api.put(`stock-transfer-out/${encodeURIComponent(mongoId)}`, payload);
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to update stock transfer out');
    }
    return response.data?.data ?? response.data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to update stock transfer out';
    throw new Error(msg);
  }
};

/**
 * DELETE /api/stock-transfer-out/:mongoId
 */
export const deleteStockTransferOut = async (mongoId) => {
  if (!mongoId) throw new Error('Missing stock transfer out id');
  try {
    const response = await api.delete(`stock-transfer-out/${encodeURIComponent(mongoId)}`);
    if (response.data?.success === false) {
      throw new Error(response.data?.message || 'Failed to delete stock transfer out');
    }
    return response.data?.data ?? response.data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to delete stock transfer out';
    throw new Error(msg);
  }
};
